/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';

import { useExitIntent, useScrollDetection } from './utils';
import OfferInboxContainer from '../../../components/GraphqlComponents/GraphqlHomePage/Partials/PersonalizedOfferInbox/OfferInboxContainer';
import { setOfferInboxData, setOfferInboxDrawerOpen } from '../../../../state/ducks/App/App-Actions';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getOfferInboxData, getPageType } from '../../../../state/ducks/App/App-Selectors';
import OfferInboxPopup from '../../../components/GraphqlComponents/GraphqlHomePage/Partials/PersonalizedOfferInbox/OfferInboxPopup/OfferInboxPopup';
import useOfferInboxPopup from '../useOfferInboxPopup';
import { getBrandName } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

const INSTANCE_ID = 'exit-offer-inbox';

export default function UserExitIntentTracker() {
    const [exitEvent, setExitEvent] = useState(false);
    const [entranceEvent, setEntranceEvent] = useState(false);
    const dispatch = useDispatch();

    const offerInbox = useSelector(getOfferInboxData);
    const brand = useSelector(getBrandName) || '1800flowers';
    const pageType = useSelector(getPageType);
    const usePopup = offerInbox?.usePopup;
    const showDefaultOffer = offerInbox?.showDefaultOffers;

    const {
        data: entranceData,
        loading: entranceLoading,
        error: entranceError,
    } = useOfferInboxPopup({
        interactionName: `${brand}-GetCampaign-OfferInboxEntrance`,
        targeting: {
            pageType,
        },
        queryName: 'Personalized_offer_entrance',
        apolloOptions: {
            skip: !entranceEvent || !usePopup || exitEvent,
        },
    });

    const hasEntranceOffer = Boolean(entranceData?.experience?.content);
    const isEntranceOpen = entranceEvent && !entranceLoading;

    useEffect(() => {
        if (!usePopup || exitEvent || sessionStorage.getItem('offer_inbox_entrance_triggered')) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setEntranceEvent(true);
        }, 5000);

        return () => clearTimeout(timer);
    }, [usePopup, exitEvent]);

    useEffect(() => {
        if (entranceError) {
            mbpLogger.logError({
                function: 'UserExitIntentTracker',
                message: 'Error loading API data from OfferInbox Entrance',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: entranceError,
            });
        }
    }, [entranceError]);

    useEffect(() => {
        if (
            !isEntranceOpen || sessionStorage.getItem('offer_inbox_entrance_triggered')
        ) {
            return;
        }

        sessionStorage.setItem('offer_inbox_entrance_triggered', 'true');
        dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            eventSubType: 'feature-tracking',
            category: 'Offer Inbox',
            label: 'entrance_offer_triggered',
            page: { type: pageType },
        }));
        dispatch(trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Offer Inbox',
            eventAction: 'Impression',
            eventLabel: 'Entrance',
        }));
    }, [
        dispatch,
        isEntranceOpen,
    ]);

    const handleExitIntent = () => {
        const hasTriggered = sessionStorage.getItem('exit_intent_triggered');
        if (hasTriggered || entranceEvent) return;

        dispatch(setOfferInboxData({
            usePopup: true,
        }));

        setExitEvent(true);
        sessionStorage.setItem('exit_intent_triggered', 'true');
        dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            eventSubType: 'feature-tracking',
            category: 'Offer Inbox',
            label: 'exit_intent_offer_triggered',
            page: { type: pageType },
        }));

        dispatch(trackEvent({
            eventCategory: 'Offer Inbox',
            eventAction: 'Impression',
            eventLabel: 'Exit Intent',
        }));
    };

    useExitIntent(handleExitIntent);
    useScrollDetection(handleExitIntent, 1);

    const { data, loading, error } = useOfferInboxPopup({
        interactionName: `${brand}-GetCampaign-OfferInboxExit`,
        targeting: {
            pageType,
        },
        queryName: 'Personalized_offer_exit',
        apolloOptions: {
            skip: !usePopup || !exitEvent,
        },
    });

    useEffect(() => {
        if (error) {
            mbpLogger.logError({
                function: 'UserExitIntentTracker',
                message: 'Error loading API data from OfferInbox Exit',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: error,
            });
        }
    }, [error]);

    useEffect(() => {
        if (showDefaultOffer) {
            dispatch(setOfferInboxDrawerOpen({ [INSTANCE_ID]: true }));
        }
    }, [showDefaultOffer]);

    if (showDefaultOffer) {
        return (
            <OfferInboxContainer
                instanceId={INSTANCE_ID}
                handleClose={() => {
                    dispatch(setOfferInboxData({
                        showDefaultOffers: false,
                    }));
                }}
            />
        );
    }

    if (isEntranceOpen && !exitEvent) {
        if (hasEntranceOffer) {
            return (
                <OfferInboxPopup
                    instanceId={INSTANCE_ID}
                    offerData={entranceData.experience}
                    handleClose={() => setEntranceEvent(false)}
                />
            );
        }

        if (entranceData?.experience?.experiment?.value?.show_offer) {
            return (
                <OfferInboxContainer
                    instanceId={INSTANCE_ID}
                    handleClose={() => setEntranceEvent(false)}
                />
            );
        }
    }

    if (exitEvent && !loading) {
        if (data?.experience?.content) {
            return (
                <OfferInboxPopup
                    instanceId={INSTANCE_ID}
                    offerData={data?.experience}
                    handleClose={() => setExitEvent(false)}
                />
            );
        }

        if (data?.experience?.experiment?.value?.show_offer) {
            return (
                <OfferInboxContainer
                    instanceId={INSTANCE_ID}
                    handleClose={() => setExitEvent(false)}
                />
            );
        }
    }

    return <></>;
}
